import type { TrackingEnvironment, TrackingInitOptions } from '../types';

export type SnippetRuntimeConfig = TrackingInitOptions & {
  /** 加载后自动上报首屏 page_view，并监听 History / hash 变化 */
  autoPageView: boolean;
};

function parseBool(value: string | null, fallback: boolean): boolean {
  if (value == null || value === '') return fallback;
  return value === '1' || value.toLowerCase() === 'true' || value === 'yes';
}

function parseNumber(value: string | null, fallback: number): number {
  if (value == null || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** 构建期由 tsup define 注入（来自 .env.development / .env.test / .env.production） */
const BUILD_DEFAULTS = {
  productId: process.env.NEXT_PUBLIC_TRACKING_PRODUCT_ID || '',
  projectId: process.env.NEXT_PUBLIC_TRACKING_PROJECT_ID || '',
  endpoint: process.env.NEXT_PUBLIC_TRACKING_ENDPOINT || '',
  environment: (process.env.NEXT_PUBLIC_TRACKING_ENV || 'development') as TrackingEnvironment,
  appVersion: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
  debug: process.env.NEXT_PUBLIC_TRACKING_DEBUG === 'true',
  batchSize: Number(process.env.NEXT_PUBLIC_TRACKING_BATCH_SIZE || 10) || 10,
  flushIntervalMs: Number(process.env.NEXT_PUBLIC_TRACKING_FLUSH_INTERVAL_MS || 5000) || 5000,
  autoPageView: process.env.NEXT_PUBLIC_TRACKING_AUTO_PAGE_VIEW !== 'false',
};

/**
 * 从当前 <script src=".../analyse-sdk?id=xxx"> 解析初始化配置。
 * 优先级：URL 查询参数 > 构建期 .env 默认值 > 同源 /api/v1/collect。
 */
export function parseScriptConfig(scriptEl?: HTMLScriptElement | null): SnippetRuntimeConfig {
  const src = scriptEl?.src || '';
  let url: URL | null = null;
  try {
    url = src ? new URL(src) : null;
  } catch {
    url = null;
  }

  const params = url?.searchParams;
  const productId =
    params?.get('id') ||
    params?.get('product_id') ||
    params?.get('productId') ||
    BUILD_DEFAULTS.productId;

  const origin = url?.origin || (typeof window !== 'undefined' ? window.location.origin : '');
  const endpoint =
    params?.get('endpoint') ||
    BUILD_DEFAULTS.endpoint ||
    (origin ? `${origin}/api/v1/collect` : '/api/v1/collect');

  const environment = (params?.get('env') ||
    params?.get('environment') ||
    BUILD_DEFAULTS.environment) as TrackingEnvironment;

  return {
    productId,
    projectId:
      params?.get('project_id') ||
      params?.get('projectId') ||
      BUILD_DEFAULTS.projectId ||
      undefined,
    endpoint,
    environment,
    appVersion:
      params?.get('app_version') || params?.get('appVersion') || BUILD_DEFAULTS.appVersion,
    debug: parseBool(params?.get('debug') ?? null, BUILD_DEFAULTS.debug),
    batchSize: parseNumber(
      params?.get('batch_size') ?? params?.get('batchSize') ?? null,
      BUILD_DEFAULTS.batchSize
    ),
    flushIntervalMs: parseNumber(
      params?.get('flush_interval_ms') ?? params?.get('flushIntervalMs') ?? null,
      BUILD_DEFAULTS.flushIntervalMs
    ),
    autoPageView: parseBool(
      params?.get('auto_page_view') ?? params?.get('autoPageView') ?? null,
      BUILD_DEFAULTS.autoPageView
    ),
  };
}

/** 在 document.scripts 中找到指向 analyse-sdk（含版本号）的 script（兼容 async 后 currentScript 为 null） */
export function findSnippetScript(): HTMLScriptElement | null {
  if (typeof document === 'undefined') return null;
  if (document.currentScript instanceof HTMLScriptElement) {
    return document.currentScript;
  }
  const scripts = Array.from(document.getElementsByTagName('script'));
  for (let i = scripts.length - 1; i >= 0; i -= 1) {
    const el = scripts[i];
    const src = el.getAttribute('src') || '';
    if (/\/analyse-sdk(?:-\d+\.\d+\.\d+)?(?:\.js)?([?#]|$)/i.test(src)) {
      return el;
    }
  }
  return null;
}
